import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import AsyncSelect from 'react-select/async';
import { GlobalContext } from '../../../library/Core/GlobalContext';
import SessionContext from '../../../library/Core/SessionContext';
import ReportChrome from './ReportChrome';
import { ABILITY_MARKER_FIELDS, ReportDef, initials } from './reportRegistry';
import { AVATAR_COLORS } from './mockReportData';
import { FilterChoice, ReportField, ReportRow, SCROLL_LIMIT, createField, downloadReport, fields, filterOptions, search, updateField, uploadReportFile } from './CustomReportService';
import { barFilters, filterCatalog, tableColumns } from './reportColumnConfig';
import { useCampus } from './campusContext';
import { useRegisterSampleFile } from './sampleFileContext';

type ReportKind = 'events' | 'rewards' | 'markers';

function assignedInstituteIds(sessionState: { userTypeCode?: string; role?: string; institutes?: unknown; primaryInstitute?: unknown } | null | undefined) {
    const codes = [sessionState?.userTypeCode, sessionState?.role];
    if (!codes.includes('INSTITUTE_ADMIN') && !codes.includes('SCHOOL_ADMIN')) return null;
    const raw = sessionState?.institutes;
    const listed = Array.isArray(raw) ? raw.filter((id) => id != null && id !== '') : [];
    const primary = sessionState?.primaryInstitute;
    const ids = listed.length ? listed : primary != null && primary !== '' ? [primary] : [];
    return new Set(ids.map((code) => String(code)));
}

function batchLabel(row: ReportRow) {
    return [row.programName, row.passOutYear].filter((part) => part != null && part !== '').join(' · ');
}

function formatDate(value?: string | null) {
    if (!value) return '';
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) return value;
    return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
    });
}

function cell(value: string | number | null | undefined) {
    if (value == null || value === '') return <span className="cr-blank">—</span>;
    return value;
}

const MARKER_TAGS = 2;

function MarkerTags({ labels }: { labels: string[] }) {
    if (!labels.length) return <span className="cr-blank">—</span>;
    const shown = labels.slice(0, MARKER_TAGS);
    const rest = labels.slice(MARKER_TAGS);
    return (
        <div className="cr-tags">
            {shown.map((label) => (
                <span key={label} className="cr-tag">
                    {label}
                </span>
            ))}
            {rest.length > 0 && (
                <span className="cr-tag cr-tag-more" tabIndex={0} title={rest.join(', ')}>
                    More
                    <span className="cr-tag-tip" role="tooltip">
                        {rest.join(', ')}
                    </span>
                </span>
            )}
        </div>
    );
}

const LiveReport = ({ report, kind }: { report: ReportDef; kind: ReportKind }) => {
    const { sessionState } = useContext(SessionContext);
    const { globalState } = useContext(GlobalContext);
    const { instituteId, setInstituteId, refreshCounts } = useCampus();
    const [campusName, setCampusName] = useState('');
    const [query, setQuery] = useState('');
    const [appliedQuery, setAppliedQuery] = useState('');
    const [rows, setRows] = useState<ReportRow[]>([]);
    const [customFields, setCustomFields] = useState<ReportField[]>([]);
    const [fieldVersion, setFieldVersion] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [pageSize, setPageSize] = useState(SCROLL_LIMIT);
    const [lastId, setLastId] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [banner, setBanner] = useState<{ tone: 'ok' | 'warn' | 'error'; title: string; details: string[] } | null>(null);
    const [exporting, setExporting] = useState(false);
    const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
    const fileRef = useRef<HTMLInputElement | null>(null);
    const [places, setPlaces] = useState<Record<string, { applied: boolean; pill: boolean }>>(() =>
        Object.fromEntries(filterCatalog(kind).map((field) => [field.key, { applied: field.startApplied, pill: field.pill }]))
    );
    const [customPills, setCustomPills] = useState<Record<number, boolean>>({});
    const [choices, setChoices] = useState<Record<string, FilterChoice[]>>({});
    const [picked, setPicked] = useState<Record<string, string[]>>({});
    const sentinel = useRef<HTMLDivElement | null>(null);
    const loadingRef = useRef(false);
    const generation = useRef(0);
    const menuKey = useRef('');

    const allowedInstitutes = useMemo(() => assignedInstituteIds(sessionState), [sessionState]);

    const instituteOptions = useMemo(() => {
        const domain = globalState?.domain?.get('INSTITUTE_DOMAIN') ?? [];
        return domain
            .filter((item: { code: string }) => !allowedInstitutes || allowedInstitutes.has(String(item.code)))
            .map((item: { code: string; value: string }) => ({ value: String(item.code), label: item.value }));
    }, [globalState, allowedInstitutes]);

    useEffect(() => {
        if (!allowedInstitutes) return;
        if (allowedInstitutes.size === 1) {
            const only = Number([...allowedInstitutes][0]);
            if (Number(instituteId) !== only) setInstituteId(Number.isFinite(only) ? only : null);
            return;
        }
        if (instituteId != null && !allowedInstitutes.has(String(instituteId))) setInstituteId(null);
    }, [allowedInstitutes, instituteId, setInstituteId]);

    useEffect(() => {
        if (!instituteId) return;
        const match = instituteOptions.find((item) => Number(item.value) === Number(instituteId));
        if (match) setCampusName(match.label);
    }, [instituteId, instituteOptions]);

    const loadInstitutes = (inputValue: string) => {
        const term = inputValue.trim();
        if (term.length < 3) return Promise.resolve([]);
        const needle = term.toUpperCase();
        return Promise.resolve(
            instituteOptions.filter((option) => option.label.toUpperCase().includes(needle)).slice(0, 20)
        );
    };

    const instituteRef = useRef(instituteId);
    useEffect(() => {
        if (instituteRef.current === instituteId) return;
        instituteRef.current = instituteId;
        setPicked({});
    }, [instituteId]);

    const mountedKind = useRef(kind);
    useEffect(() => {
        if (mountedKind.current === kind) return;
        mountedKind.current = kind;
        setPlaces(Object.fromEntries(filterCatalog(kind).map((field) => [field.key, { applied: field.startApplied, pill: field.pill }])));
        setCustomPills({});
        setPicked({});
    }, [kind]);

    const filterBody = useMemo(() => {
        const filters: Record<string, string[] | Record<string, string[]>> = {};
        const custom: Record<string, string[]> = {};
        for (const [key, values] of Object.entries(picked)) {
            if (!values.length) continue;
            if (key.startsWith('custom:')) {
                custom[key.slice(7)] = values;
                continue;
            }
            if (key === 'markers') {
                filters.markers = values;
                continue;
            }
            filters[key] = values;
        }
        if (Object.keys(custom).length) filters.custom = custom;
        return filters;
    }, [picked]);
    const filterKey = JSON.stringify(filterBody);

    useEffect(() => {
        const timer = window.setTimeout(() => setAppliedQuery(query.trim()), 300);
        return () => window.clearTimeout(timer);
    }, [query]);

    const loadMenus = (requestKind: ReportKind, requestInstitute: number | null, requestVersion: number) => {
        const key = `${requestKind}:${requestInstitute ?? 'all'}:${requestVersion}`;
        if (menuKey.current === key) return;
        menuKey.current = key;
        filterOptions(requestKind, requestInstitute)
            .then((response) => {
                if (menuKey.current !== key) return;
                const data = response.data || {};
                const next: Record<string, FilterChoice[]> = { ...(data.options || {}) };
                const custom = data.custom || {};
                for (const [id, list] of Object.entries(custom)) {
                    next[`custom:${id}`] = list as FilterChoice[];
                }
                setChoices(next);
            })
            .catch(() => {
                if (menuKey.current === key) setChoices({});
            });
    };

    useEffect(() => {
        let cancelled = false;
        fields(kind, instituteId)
            .then((response) => {
                if (cancelled) return;
                const fieldList: ReportField[] = response.data?.fields || [];
                setCustomFields(fieldList.filter((field) => field.sourceKind === 'CUSTOM'));
            })
            .catch(() => {
                if (!cancelled) setCustomFields([]);
            });
        return () => {
            cancelled = true;
        };
    }, [kind, instituteId, fieldVersion]);

    useEffect(() => {
        let cancelled = false;
        const requestId = generation.current + 1;
        generation.current = requestId;
        setLoading(true);
        setError('');
        setHasMore(false);
        loadingRef.current = true;
        search(kind, { ...(instituteId ? { instituteId } : {}), lastId: 0, q: appliedQuery, filters: filterBody })
            .then((searchResponse) => {
                if (cancelled || generation.current !== requestId) return;
                const page = searchResponse.data;
                setRows(page.rows || []);
                setHasMore(Boolean(page.hasMore));
                setPageSize(Number(page.limit) || SCROLL_LIMIT);
                setLastId(Number(page.lastId) || 0);
            })
            .catch((err) => {
                if (cancelled || generation.current !== requestId) return;
                setRows([]);
                setHasMore(false);
                setError(err?.data?.message || err?.message || 'Could not load this report.');
            })
            .finally(() => {
                if (cancelled || generation.current !== requestId) return;
                setLoading(false);
                loadingRef.current = false;
                loadMenus(kind, instituteId, fieldVersion);
            });
        return () => {
            cancelled = true;
        };
    }, [kind, instituteId, appliedQuery, fieldVersion, filterKey]);

    useEffect(() => {
        const node = sentinel.current;
        if (!node || !hasMore) return;
        let seenHidden = false;
        const observer = new IntersectionObserver((entries) => {
            const visible = Boolean(entries[0]?.isIntersecting);
            if (!visible) {
                seenHidden = true;
                return;
            }
            if (!seenHidden || loadingRef.current) return;
            seenHidden = false;
            loadingRef.current = true;
            setLoading(true);
            const requestId = generation.current;
            search(kind, { ...(instituteId ? { instituteId } : {}), lastId, q: appliedQuery, filters: filterBody })
                .then((response) => {
                    if (generation.current !== requestId) return;
                    const page = response.data;
                    setRows((prev) => [...prev, ...(page.rows || [])]);
                    setHasMore(Boolean(page.hasMore));
                    setPageSize(Number(page.limit) || SCROLL_LIMIT);
                    setLastId(Number(page.lastId) || lastId);
                })
                .catch((err) => {
                    if (generation.current !== requestId) return;
                    setHasMore(false);
                    setError(err?.data?.message || err?.message || 'Could not load the next rows.');
                })
                .finally(() => {
                    if (generation.current !== requestId) return;
                    setLoading(false);
                    loadingRef.current = false;
                });
        });
        observer.observe(node);
        return () => observer.disconnect();
    }, [hasMore, instituteId, kind, lastId, appliedQuery, filterKey]);

    const openUpload = useCallback(() => {
        fileRef.current?.click();
    }, []);

    useRegisterSampleFile(kind, customFields.map((field) => field.label), openUpload, Boolean(instituteId), true);

    const onUploadFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        if (!instituteId) {
            setBanner({ tone: 'error', title: 'Select a campus, then choose the Excel file again.', details: [] });
            return;
        }
        setBanner({ tone: 'ok', title: 'Uploading…', details: [] });
        setError('');
        try {
            const response = await uploadReportFile(kind, instituteId, file);
            const data = response.data || {};
            const details: string[] = [];
            const unknown = Array.isArray(data.unknownHeaders) ? data.unknownHeaders.filter(Boolean) : [];
            if (unknown.length) {
                details.push(`These columns were ignored because this report does not use them: ${unknown.join(', ')}.`);
            }
            for (const item of Array.isArray(data.errors) ? data.errors : []) {
                details.push(item.row ? `Row ${item.row}: ${item.message}` : item.message);
            }
            if (data.errorsTruncated) details.push('Showing the first 50 problems. Fix those and upload the file again.');
            const skipped = Number(data.rowsSkipped) || 0;
            setBanner({
                tone: skipped || details.length ? 'warn' : 'ok',
                title: `Added ${data.rowsAdded || 0}, updated ${data.rowsUpdated || 0}, skipped ${skipped}.`,
                details,
            });
            setFieldVersion((version) => version + 1);
            refreshCounts();
        } catch (err: any) {
            setBanner({
                tone: 'error',
                title: 'The file was not uploaded.',
                details: [err?.data?.message || err?.message || 'Upload failed.'],
            });
        }
    };

    const onExport = async () => {
        setExporting(true);
        try {
            await downloadReport(kind, { ...(instituteId ? { instituteId } : {}), q: appliedQuery, filters: filterBody });
            setBanner({
                tone: 'ok',
                title: `Downloaded ${kind}-export.xlsx for the rows matching the current search and filters.`,
                details: [],
            });
        } catch (err: any) {
            let message = err?.data?.message || err?.message || 'Export failed.';
            if (err?.data instanceof Blob) {
                try {
                    const parsed = JSON.parse(await err.data.text());
                    if (parsed.message) message = parsed.message;
                } catch {
                    message = 'Export failed.';
                }
            }
            setBanner({ tone: 'error', title: 'Export failed.', details: [message] });
        } finally {
            setExporting(false);
        }
    };

    const shownCustom = customFields.filter((field) => Number(field.showAsColumn) === 1);
    const allStandard = tableColumns(kind);
    const standardColumns = allStandard.filter((column) => !hiddenColumns.includes(column.key));
    const availableStandard = allStandard.filter((column) => hiddenColumns.includes(column.key));
    const columns = [
        ...standardColumns.map((column) => column.label),
        ...shownCustom.map((field) => field.label),
    ];

    const changeVisibility = async (id: number, patch: { showAsColumn?: boolean; showAsFilter?: boolean }) => {
        if (!instituteId) return;
        await updateField(kind, id, { instituteId, ...patch });
        setFieldVersion((version) => version + 1);
    };

    const dataTypeFor = (key: string) => {
        if (key === 'event_name' || key === 'title') return 'SUGGEST';
        if (key === 'event_date') return 'DATE';
        if (key === 'pass_out_year' || key === 'award_year') return 'NUMBER';
        return 'SELECT';
    };
    const managedFilters = [
        ...filterCatalog(kind).map((field) => {
            const place = places[field.key] || { applied: field.startApplied, pill: field.pill };
            return {
                key: field.key,
                label: field.label,
                group: field.group === 'report' ? ('Report-specific' as const) : ('Standard' as const),
                bucket: field.group,
                control: field.control,
                dataType: dataTypeFor(field.key),
                applied: place.applied,
                pill: place.pill,
            };
        }),
        ...customFields.map((field) => ({
            key: `custom:${field.id}`,
            label: field.label,
            group: 'Custom' as const,
            bucket: 'custom' as const,
            control: 'pill' as const,
            dataType: field.dataType || 'TEXT',
            applied: Number(field.showAsFilter) === 1,
            pill: customPills[field.id] !== false,
        })),
    ];

    const setFilterApplied = (key: string, applied: boolean) => {
        if (key.startsWith('custom:')) {
            void changeVisibility(Number(key.slice(7)), { showAsFilter: applied });
            return;
        }
        setPlaces((prev) => ({
            ...prev,
            [key]: { ...(prev[key] || { applied: true, pill: true }), applied, ...(applied ? { pill: true } : {}) },
        }));
        if (!applied) {
            if (key === 'markers') {
                setPicked((prev) => ({ ...prev, markers: [] }));
                return;
            }
            const marker = ABILITY_MARKER_FIELDS.find((field) => field.code === key);
            if (marker) {
                setPicked((prev) => ({
                    ...prev,
                    [marker.category]: (prev[marker.category] || []).filter((code) => code !== key),
                }));
                return;
            }
            setPicked((prev) => ({ ...prev, [key]: [] }));
        }
    };

    const setFilterPill = (key: string, pill: boolean) => {
        if (key.startsWith('custom:')) {
            const id = Number(key.slice(7));
            setCustomPills((prev) => ({ ...prev, [id]: pill }));
            return;
        }
        setPlaces((prev) => ({
            ...prev,
            [key]: { ...(prev[key] || { applied: true, pill: false }), pill },
        }));
    };

    const placed = managedFilters.filter((item) => item.applied);
    const markerKeys = new Set(ABILITY_MARKER_FIELDS.map((field) => field.code));
    const markerChoices = ABILITY_MARKER_FIELDS.map((field) => ({ value: field.code, label: field.label }));
    const suggestNames = useCallback(
        (key: string, q: string) =>
            filterOptions(kind, instituteId, { suggest: key, q }).then(
                (response) => (response.data?.suggestions || []) as FilterChoice[]
            ),
        [kind, instituteId]
    );

    const customReportFilters = placed
        .filter((item) => item.bucket === 'custom' && item.pill)
        .map((item) => ({ key: item.key, label: item.label, dataType: item.dataType }));
    const filterBar = {
        top: placed
            .filter((item) => item.bucket === 'common')
            .map((item) => ({ key: item.key, label: item.label, dataType: item.dataType, control: item.control === 'institute' ? ('institute' as const) : ('pill' as const) })),
        report:
            kind === 'markers'
                ? [{ key: 'markers', label: 'Markers', removable: false, dataType: 'SELECT' }, ...customReportFilters]
                : placed
                      .filter((item) => item.bucket !== 'common' && !markerKeys.has(item.key))
                      .map((item) => ({ key: item.key, label: item.label, dataType: item.dataType })),
        more: [] as { key: string; label: string }[],
        onRemove: (key: string) => setFilterApplied(key, false),
        choices: kind === 'markers' ? { ...choices, markers: markerChoices } : choices,
        selected: picked,
        onToggle: (key: string, value: string) => {
            setPicked((prev) => {
                const current = prev[key] || [];
                const next = current.includes(value) ? current.filter((item) => item !== value) : [...current, value];
                return { ...prev, [key]: next };
            });
        },
        onCommit: (key: string, value: string) => {
            const text = value.trim();
            setPicked((prev) => ({ ...prev, [key]: text ? [text] : [] }));
        },
        onSuggest: suggestNames,
    };

    const standardCell = (key: string, row: ReportRow) => {
        switch (key) {
            case 'alumni':
                return null;
            case 'program_batch':
                return cell(batchLabel(row));
            case 'event_name':
                return cell(row.eventName);
            case 'event_type':
                return row.eventType ? <span className="cr-tag">{row.eventType}</span> : cell(null);
            case 'role':
                return row.role ? <span className="cr-tag cr-tag-grey">{row.role}</span> : cell(null);
            case 'event_date':
                return cell(formatDate(row.eventDate));
            case 'mode':
                return cell(row.mode);
            case 'title':
                return cell(row.title);
            case 'awarding_body':
                return cell(row.awardingBody);
            case 'category':
                return row.category ? <span className="cr-tag cr-tag-grey">{row.category}</span> : cell(null);
            case 'level':
                return row.level ? <span className="cr-tag">{row.level}</span> : cell(null);
            case 'award_year':
                return cell(row.awardYear);
            case 'markers':
                return <MarkerTags labels={row.markers || []} />;
            default:
                return cell(null);
        }
    };

    const countNoun = kind === 'events' ? 'participations' : kind === 'rewards' ? 'awards' : 'alumni';
    const countText = loading && rows.length === 0
        ? 'Loading…'
        : `Showing ${rows.length}${hasMore ? '+' : ''} ${countNoun}`;

    const bannerNode = banner ? (
        <div className={`cr-upload-note ${banner.tone}`}>
            <div className="cr-upload-note-head">
                <strong>{banner.title}</strong>
                <button type="button" onClick={() => setBanner(null)}>
                    Dismiss
                </button>
            </div>
            {banner.details.length > 0 && (
                <ul>
                    {banner.details.map((line, index) => (
                        <li key={`${index}-${line}`}>{line}</li>
                    ))}
                </ul>
            )}
        </div>
    ) : null;

    return (
        <>
        <input ref={fileRef} type="file" accept=".xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" hidden onChange={onUploadFile} />
        <ReportChrome
            report={report}
            countText={countText}
            sortOptions={[]}
            columns={columns}
            live
            banner={bannerNode}
            onExport={onExport}
            exportBusy={exporting}
            hideHeading
            hideCustomField={kind === 'markers'}
            searchValue={query}
            onSearchChange={setQuery}
            barFilters={barFilters(kind)}
            filterBar={filterBar}
            columnSetup={{
                standard: standardColumns.map((column) => ({ key: column.key, label: column.label })),
                availableStandard: availableStandard.map((column) => ({ key: column.key, label: column.label })),
                onShowColumn: (key) => setHiddenColumns((current) => current.filter((item) => item !== key)),
                onHideColumn: (key) => setHiddenColumns((current) => (current.includes(key) ? current : [...current, key])),
                filters: barFilters(kind).map((field) => field.label),
                filterItems: managedFilters
                    .filter((item) => kind !== 'markers' || !markerKeys.has(item.key))
                    .map((item) => ({
                    key: item.key,
                    label: item.label,
                    group: item.group,
                    applied: item.applied,
                    pill: item.pill,
                })),
                onFilterApplied: setFilterApplied,
                onFilterPill: setFilterPill,
                markerCategories:
                    kind === 'markers'
                        ? [
                              { value: 'workProfile', label: 'Work Profile' },
                              { value: 'demographics', label: 'Demographics' },
                              { value: 'positionStrength', label: 'Position Strength' },
                          ]
                        : undefined,
                custom: customFields.map((field) => ({
                    id: field.id,
                    label: field.label,
                    showAsColumn: field.showAsColumn,
                    showAsFilter: field.showAsFilter,
                    dataType: field.dataType,
                })),
                onCreate: async (input) => {
                    if (!instituteId) throw new Error('Select an institute first');
                    await createField(kind, { instituteId, ...input });
                    setFieldVersion((version) => version + 1);
                },
                onChangeVisibility: changeVisibility,
            }}
            campusSlot={
                <div className="cr-campus-search">
                    <AsyncSelect
                        inputId="cr-institute-name"
                        classNamePrefix="cr-campus"
                        aria-label="Campus"
                        cacheOptions
                        components={{ IndicatorSeparator: () => null }}
                        menuPosition="fixed"
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                        styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 40 }),
                            control: (base, state) => ({
                                ...base,
                                minHeight: 36,
                                height: 36,
                                border: '1px solid #98a2b3',
                                borderRadius: 6,
                                boxShadow: state.isFocused ? '0 0 0 3px rgba(26, 39, 68, 0.12)' : 'none',
                                background: '#fff',
                            }),
                            valueContainer: (base) => ({ ...base, padding: '0 10px', height: 34 }),
                            placeholder: (base) => ({ ...base, color: '#667085', fontSize: 13 }),
                            singleValue: (base) => ({ ...base, color: '#2b3247', fontSize: 13 }),
                            input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: 13 }),
                            indicatorsContainer: (base) => ({ ...base, height: 34 }),
                            dropdownIndicator: (base) => ({ ...base, padding: '0 8px', color: '#667085' }),
                        }}
                        loadOptions={loadInstitutes}
                        value={
                            instituteId
                                ? { value: String(instituteId), label: campusName || String(instituteId) }
                                : null
                        }
                        onChange={(option) => {
                            if (!option) {
                                setInstituteId(null);
                                setCampusName('');
                                return;
                            }
                            setInstituteId(Number(option.value));
                            setCampusName(option.label);
                        }}
                        placeholder="Search campus"
                        isClearable
                        noOptionsMessage={({ inputValue }) =>
                            inputValue.trim().length < 3 ? 'Type 3 letters to search' : 'No institute found'
                        }
                    />
                </div>
            }>
            {rows.map((row, index) => (
                <tr key={row.id}>
                    <td>
                        <input type="checkbox" className="form-check-input" readOnly />
                    </td>
                    {standardColumns.map((column) =>
                        column.key === 'alumni' ? (
                            <td key={column.key}>
                                <div className="cr-alum">
                                    <div className="cr-avatar" style={{ background: AVATAR_COLORS[index % AVATAR_COLORS.length] }}>
                                        {initials(row.name || '?')}
                                    </div>
                                    <div>
                                        <div className="cr-alum-name">{row.name || '—'}</div>
                                        <div className="cr-alum-sub">{batchLabel(row)}</div>
                                    </div>
                                </div>
                            </td>
                        ) : (
                            <td key={column.key}>{standardCell(column.key, row)}</td>
                        )
                    )}
                    {shownCustom.map((field) => (
                        <td key={field.fieldKey}>{cell(row.custom?.[field.fieldKey])}</td>
                    ))}
                </tr>
            ))}
            {!loading && rows.length === 0 && (
                <tr>
                    <td colSpan={columns.length + 1}>
                        <div className="cr-soon">
                            <p>{error || (instituteId ? 'No rows for this campus.' : 'No rows.')}</p>
                        </div>
                    </td>
                </tr>
            )}
            <tr>
                <td colSpan={columns.length + 1}>
                    <div ref={sentinel} className="cr-scroll-status">
                        {loading && rows.length > 0 ? `Loading the next ${pageSize}…` : hasMore ? `Scroll for the next ${pageSize}` : rows.length > 0 ? 'End of list' : ''}
                    </div>
                </td>
            </tr>
        </ReportChrome>
        </>
    );
};

export default LiveReport;
